import type { LucideIcon } from "lucide-react";
import { LinkEntityPopover, LinkedEntityRow } from "@/components/directory/LinkEntityPopover";
import type { DirectoryGroupData } from "@/hooks/use-claim-directory-links";

interface DirectoryGroupProps extends DirectoryGroupData {
    icon: LucideIcon;
    title: string;
    showBadge?: boolean;
    canManage: boolean;
    placeholder: string;
    createNewLabel: string;
    dialogTitle: string;
    emptyMessage: string;
}

export function DirectoryGroup({
    icon: Icon,
    title,
    showBadge = true,
    canManage,
    placeholder,
    createNewLabel,
    dialogTitle,
    emptyMessage,
    items,
    existingIds,
    onSearch,
    onSelect,
    onCreateNew,
}: DirectoryGroupProps) {
    return (
        <div className="space-y-2">
            <div className="flex items-center flex-wrap gap-1">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide mr-auto">
                    <Icon className="h-3.5 w-3.5" />
                    {title}
                    <span className="text-muted-foreground/70">({items.length})</span>
                </div>
                {canManage && (
                    <LinkEntityPopover
                        compact
                        existingIds={existingIds}
                        onSearch={onSearch}
                        onSelect={onSelect}
                        placeholder={placeholder}
                        icon={Icon}
                        onCreateNew={onCreateNew}
                        createNewLabel={createNewLabel}
                        asDialog
                        dialogTitle={dialogTitle}
                    />
                )}
            </div>
            {items.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">{emptyMessage}</p>
            ) : (
                <div className="space-y-1.5">
                    {items.map((item) => (
                        <LinkedEntityRow
                            key={item.key}
                            icon={Icon}
                            label={item.label}
                            badge={showBadge ? item.badge : undefined}
                            onNavigate={item.onNavigate}
                            onUnlink={item.onUnlink}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
